import i18n, { type Resource } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

/**
 * Namespaces are discovered, not registered.
 *
 * A module that adds `locales/en/giving.json` gets a working namespace without
 * editing this file, and a locale that is missing a namespace another locale has
 * shows up as a build-time-visible gap rather than a blank screen at runtime.
 *
 * ICU MessageFormat rather than i18next's own plural syntax, because "1 member" /
 * "3 members" is the easy case; the languages Trinity will add have plural
 * categories that hand-written conditionals get wrong.
 */
const files = import.meta.glob<{ default: Record<string, unknown> }>(
  '../locales/*/*.json',
  { eager: true },
);

const resources: Resource = {};

for (const [path, module] of Object.entries(files)) {
  const match = /\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
  if (!match) continue;

  const [, locale, namespace] = match as unknown as [string, string, string];
  resources[locale] ??= {};
  resources[locale][namespace] = module.default;
}

export const AVAILABLE_LOCALES = Object.keys(resources).sort();

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: Object.keys(resources['en'] ?? {}),
    interpolation: { escapeValue: false },
    returnNull: false,
    // A missing key is a bug. In development it should be loud; in production it
    // should degrade to the key's own text rather than an empty element.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: import.meta.env.DEV
      ? (_lngs, ns, key) => {
          console.warn(`[i18n] missing key ${ns}:${key}`);
        }
      : undefined,
  });

export default i18n;
